import { Controller } from "@hotwired/stimulus"

// Drives unties/confirmations/edit. Three values: `easypost` (which address card is selected -
// clicking a card or its radio both set it), `requiresManualConfirm` (from the gift's country,
// fixed at page load) and `manuallyConfirmed` (the "Yes, my address is correct" checkbox, only
// consulted when requiresManualConfirm).
//
// Initial visibility is server-rendered from the same booleans, so the change callbacks firing
// once on connect just reproduce what is already on the page. The disabled state is deliberately
// NOT server-rendered: it is applied here, in JS, so a page rendered without JS is not left with a
// permanently disabled button.
export default class extends Controller {
  static get targets() {
    return ["unverifiedCard", "verifiedCard", "modalCard", "unverifiedRadio", "verifiedRadio",
      "warning", "nextLink", "nextButton"];
  }

  static get values() {
    return {
      easypost: String,
      requiresManualConfirm: Boolean,
      manuallyConfirmed: { type: Boolean, default: false }
    };
  }

  // "card-selected" is styled only by the two address-page stylesheets (unties/address_verification.scss,
  // v2/pages/unties/address.scss) and used nowhere else in the app -- genuinely controller-owned, on
  // this controller's single mount (unties/confirmations/edit). "hidden" (below) is Bootstrap's global
  // utility class and is deliberately left as a literal -- see decisions.md D1.
  static get classes() {
    return ["cardSelected"];
  }

  get cardSelectedClassName() {
    return this.hasCardSelectedClass ? this.cardSelectedClass : "card-selected";
  }

  selectUnverified() {
    this.easypostValue = "false";
  }

  selectVerified() {
    this.easypostValue = "true";
  }

  toggleManualConfirm(event) {
    this.manuallyConfirmedValue = event.target.checked;
  }

  easypostValueChanged() {
    const verified = this.easypostValue === "true";

    if (this.hasVerifiedCardTarget) { this.verifiedCardTarget.classList.toggle(this.cardSelectedClassName, verified); }
    if (this.hasUnverifiedCardTarget) { this.unverifiedCardTarget.classList.toggle(this.cardSelectedClassName, !verified); }
    if (this.hasModalCardTarget) { this.modalCardTarget.classList.toggle(this.cardSelectedClassName, !verified); }

    if (this.hasVerifiedRadioTarget) { this.verifiedRadioTarget.checked = verified; }
    if (this.hasUnverifiedRadioTarget) { this.unverifiedRadioTarget.checked = !verified; }

    if (this.hasWarningTarget) { this.warningTarget.classList.toggle("hidden", verified); }
    if (this.hasNextLinkTarget) { this.nextLinkTarget.classList.toggle("hidden", verified); }
    if (this.hasNextButtonTarget) { this.nextButtonTarget.classList.toggle("hidden", !verified); }
  }

  manuallyConfirmedValueChanged() {
    const disabled = this.requiresManualConfirmValue && !this.manuallyConfirmedValue;

    if (this.hasNextLinkTarget) { this.toggleDisabled(this.nextLinkTarget, disabled); }
    if (this.hasNextButtonTarget) { this.toggleDisabled(this.nextButtonTarget, disabled); }
  }

  // The real `disabled` attribute, not a CSS class: anchors have no native disabled semantics,
  // so Bootstrap's `.btn[disabled]` rule is what actually blocks the click.
  toggleDisabled(el, disabled) {
    if (disabled) {
      el.setAttribute("disabled", "disabled");
    } else {
      el.removeAttribute("disabled");
    }
  }
}
